import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useMerchantId } from './useMerchantId';
import { useToast } from './use-toast';
import i18n from '@/i18n';

type StoreBranchRequest = components['schemas']['StoreBranchRequest'];
type UpdateBranchRequest = components['schemas']['UpdateBranchRequest'];
type StoreDeviceRequest = components['schemas']['StoreDeviceRequest'];
type UpdateDeviceRequest = components['schemas']['UpdateDeviceRequest'];

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message) || fallback;
  }
  return fallback;
}

/**
 * FR-64: replaces the dead `manage-branches` edge function's branch actions
 * (create/update/delete) with the real `/branches` endpoints. Shared between
 * `BranchesTab` (profile) and the standalone `Branches` page — both offer
 * identical branch CRUD, just with different form fields wired up.
 */
export function useBranchMutations() {
  const { merchantId } = useMerchantId();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['branches', merchantId] });

  const createBranch = useMutation({
    mutationFn: async (body: StoreBranchRequest) => {
      const { data, error } = await apiClient.POST('/branches', { body });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.add_error')));
      return data?.data;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: i18n.t('misc_branch_mut.added') });
    },
    onError: (err: Error) => toast({ title: i18n.t('misc_branch_mut.error'), description: err.message, variant: 'destructive' }),
  });

  const updateBranch = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: UpdateBranchRequest }) => {
      const { data, error } = await apiClient.PATCH('/branches/{branch}', {
        params: { path: { branch: id } },
        body,
      });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.update_error')));
      return data?.data;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: i18n.t('misc_branch_mut.updated') });
    },
    onError: (err: Error) => toast({ title: i18n.t('misc_branch_mut.error'), description: err.message, variant: 'destructive' }),
  });

  /** Toggle-active uses the same `PATCH /branches/{branch}` endpoint, just silently (no toast). */
  const toggleBranch = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      const { error } = await apiClient.PATCH('/branches/{branch}', {
        params: { path: { branch: id } },
        body: { is_active },
      });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.error')));
    },
    onSuccess: invalidate,
  });

  const deleteBranch = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.DELETE('/branches/{branch}', { params: { path: { branch: id } } });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.delete_error')));
    },
    onSuccess: () => {
      invalidate();
      toast({ title: i18n.t('misc_branch_mut.deleted') });
    },
    onError: (err: Error) => toast({ title: i18n.t('misc_branch_mut.error'), description: err.message, variant: 'destructive' }),
  });

  return { createBranch, updateBranch, toggleBranch, deleteBranch };
}

/** FR-64: replaces `manage-branches`'s `*_device` actions with `/devices`. */
export function useDeviceMutations() {
  const { merchantId } = useMerchantId();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['devices', merchantId] });

  const createDevice = useMutation({
    mutationFn: async (body: StoreDeviceRequest) => {
      const { data, error } = await apiClient.POST('/devices', { body });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.dev_add_error')));
      return data?.data;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: i18n.t('misc_branch_mut.dev_added') });
    },
    onError: (err: Error) => toast({ title: i18n.t('misc_branch_mut.error'), description: err.message, variant: 'destructive' }),
  });

  const updateDevice = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: UpdateDeviceRequest }) => {
      const { data, error } = await apiClient.PATCH('/devices/{device}', {
        params: { path: { device: id } },
        body,
      });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.dev_update_error')));
      return data?.data;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: i18n.t('misc_branch_mut.dev_updated') });
    },
    onError: (err: Error) => toast({ title: i18n.t('misc_branch_mut.error'), description: err.message, variant: 'destructive' }),
  });

  const toggleDevice = useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      const { error } = await apiClient.PATCH('/devices/{device}', {
        params: { path: { device: id } },
        body: { is_active },
      });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.error')));
    },
    onSuccess: invalidate,
  });

  const deleteDevice = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.DELETE('/devices/{device}', { params: { path: { device: id } } });
      if (error) throw new Error(errorMessage(error, i18n.t('misc_branch_mut.dev_delete_error')));
    },
    onSuccess: () => {
      invalidate();
      toast({ title: i18n.t('misc_branch_mut.dev_deleted') });
    },
    onError: (err: Error) => toast({ title: i18n.t('misc_branch_mut.error'), description: err.message, variant: 'destructive' }),
  });

  return { createDevice, updateDevice, toggleDevice, deleteDevice };
}
